import * as React from 'react';
import { useSelector } from 'react-redux';

import { useAppDispatch } from 'shared/hooks/useAppDispatch';
import { useSendCPDAnalytics } from 'shared/hooks/useSendCPDAnalytics';
import {
  changeModalType,
  closeCPDModal,
  setCpdDealType,
  setCpdDraftSource,
  setCpdPickLandingChannel,
} from 'shared/actions/cpd';
import { ECPDEntrySource, ECPDModalType, EDealType } from 'shared/types/redux/cpd';
import { selectCPDModalType, selectIsCurrentGeoInCPD, selectPikLandingChannel } from 'shared/selectors/cpd';
import { selectIsUserInCPD } from 'shared/selectors/user';
import { getCurrentTab } from 'shared/selectors/tabs';
import {
  clearCPDTimestamp,
  getCPDDraftSourceByCurrentTab,
  getCPDPikLandingChannelByCurrentTab,
  getDontShowModalTimeElapsed,
  setCPDTimestamp,
} from 'shared/utils/cpd';
import { CPL_FORM_URL, PIK_LANDING_URL } from 'shared/constants/cpd';

export function useGetCPDModalOptions() {
  const dispatch = useAppDispatch();
  const isUserInCPD = useSelector(selectIsUserInCPD);
  const isCurrentGeoInCPD = useSelector(selectIsCurrentGeoInCPD);
  const cpdModalType = useSelector(selectCPDModalType);
  const currentTab = useSelector(getCurrentTab);
  const pikLandingChannel = useSelector(selectPikLandingChannel);
  const { sendCPDModalEvent } = useSendCPDAnalytics();

  /**
   * юзер соб или поискун + гео подходит под CPD + флаг видимости мод. окна
   */
  const isModalVisible = isUserInCPD && isCurrentGeoInCPD && cpdModalType === ECPDModalType.Initial;

  React.useEffect(() => {
    const isShowRestrictionTimeElapsed = getDontShowModalTimeElapsed();

    if (isCurrentGeoInCPD && isShowRestrictionTimeElapsed) {
      const pikLandingChannel = getCPDPikLandingChannelByCurrentTab(currentTab, ECPDEntrySource.Modal);
      const cpdDraftSource = getCPDDraftSourceByCurrentTab(currentTab, ECPDEntrySource.Modal);

      clearCPDTimestamp();
      dispatch(changeModalType(ECPDModalType.Initial));
      dispatch(setCpdPickLandingChannel(pikLandingChannel));
      dispatch(setCpdDraftSource(cpdDraftSource));
    }
    /**
     * Смена региона фильтров, или смена вкладки должны приводить к показу модалки.
     * При этом смена региона с CPD на CPD не приводит к показу попапа, это согласовано с продуктом.
     */
  }, [dispatch, isCurrentGeoInCPD, currentTab]);

  React.useEffect(() => {
    if (isModalVisible) {
      sendCPDModalEvent({ action: 'Show_popup', label: 'RealtorHelp' });
    }
  }, [isModalVisible, sendCPDModalEvent]);

  const onModalClose = React.useCallback(() => {
    dispatch(closeCPDModal());
  }, [dispatch]);

  const onSellClick = React.useCallback(() => {
    sendCPDModalEvent({ action: 'Click_popup', label: 'RealtorHelpSell' });

    dispatch(changeModalType(ECPDModalType.PrelandingHomeowner));
    dispatch(setCpdDealType(EDealType.Sale));
  }, [dispatch, sendCPDModalEvent]);

  const onSellAndBuyNewClick = React.useCallback(() => {
    sendCPDModalEvent({ action: 'Click_popup', label: 'RealtorHelpSellAndBuy' });

    dispatch(changeModalType(ECPDModalType.PrelandingHomeowner));
    dispatch(setCpdDealType(EDealType.Sale));
  }, [dispatch, sendCPDModalEvent]);

  const onBuyClick = React.useCallback(() => {
    sendCPDModalEvent({ action: 'Click_popup', label: 'RealtorHelpBuy' });

    dispatch(changeModalType(ECPDModalType.PrelandingSearcher));
    dispatch(setCpdDealType(EDealType.Buy));
  }, [dispatch, sendCPDModalEvent]);

  const onRentClick = React.useCallback(() => {
    sendCPDModalEvent({ action: 'Click_popup', label: 'RealtorHelpRent' });

    window.location.href = `${PIK_LANDING_URL}?channel=${pikLandingChannel ?? ''}`;
  }, [pikLandingChannel, sendCPDModalEvent]);

  const onAnotherClick = React.useCallback(() => {
    sendCPDModalEvent({ action: 'Click_popup', label: 'RealtorHelpOther' });

    window.location.href = CPL_FORM_URL;
  }, [sendCPDModalEvent]);

  const onDontShowAgainClick = React.useCallback(() => {
    dispatch(closeCPDModal());

    setCPDTimestamp();
  }, [dispatch]);

  return React.useMemo(
    () => ({
      isModalVisible,
      onModalClose,
      onDontShowAgainClick,
      onSellClick,
      onSellAndBuyNewClick,
      onBuyClick,
      onRentClick,
      onAnotherClick,
    }),
    [
      isModalVisible,
      onModalClose,
      onDontShowAgainClick,
      onSellClick,
      onSellAndBuyNewClick,
      onBuyClick,
      onRentClick,
      onAnotherClick,
    ],
  );
}
